import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency } from '@/lib/utils';
import {
    Gavel, ShoppingBag, AlertTriangle, Users, Package,
    TrendingUp, TrendingDown, DollarSign, Wallet, ArrowRight, CheckCircle, XCircle, Flag, Globe,
    Mail, Clock, Send, RefreshCw, Loader2, Calendar
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminDashboardTab = ({ 
    totals, 
    dashboard, 
    chartPeriod, 
    setChartPeriod, 
    onNavigate 
}) => {
    const [digestStatus, setDigestStatus] = useState(null);
    const [loadingDigest, setLoadingDigest] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [forcingProcess, setForcingProcess] = useState(false);

    // Fetch digest status on mount
    useEffect(() => {
        fetchDigestStatus();
    }, []);

    const fetchDigestStatus = async () => {
        setLoadingDigest(true);
        try {
            const res = await api.get('/admin/digest/status');
            setDigestStatus(res.data);
        } catch (error) {
            console.error('Error fetching digest status:', error);
        } finally {
            setLoadingDigest(false);
        }
    };

    const sendTestDigest = async () => {
        if (!testEmail) {
            toast.error('Introdu o adresă de email');
            return;
        }
        setSendingTest(true);
        try {
            const res = await api.post('/admin/digest/test', {
                to_email: testEmail,
                digest_type: 'daily'
            });
            if (res.data.status === 'sent') {
                toast.success(res.data.message);
            } else if (res.data.status === 'mocked') {
                toast.info(res.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea digest-ului');
        } finally {
            setSendingTest(false);
        }
    };

    const forceProcessDigest = async () => {
        setForcingProcess(true);
        try {
            const res = await api.post('/admin/digest/force-process');
            toast.success(`${res.data.message}: ${res.data.processed} trimise`);
            fetchDigestStatus();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la procesare');
        } finally {
            setForcingProcess(false);
        }
    };
    const getPeriodLabel = (period) => {
        const labels = {
            '7d': '7 zile',
            '30d': '30 zile',
            '90d': '90 zile',
            '180d': '6 luni',
            '1y': '1 an'
        };
        return labels[period] || period;
    };

    return (
        <div className="space-y-6">
            {/* Active Listings Stats */}
            <div>
                <h3 className="text-lg font-semibold mb-4">Statistici Generale</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-purple-50 border-purple-200" onClick={() => onNavigate('users')}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Users className="w-8 h-8 text-purple-500" />
                                <ArrowRight className="w-4 h-4 text-purple-400" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-purple-800">{totals.total_users || 0}</p>
                            <p className="text-sm text-purple-600">Utilizatori totali</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-blue-50 border-blue-200" onClick={() => onNavigate('listings')}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Package className="w-8 h-8 text-blue-500" />
                                <ArrowRight className="w-4 h-4 text-blue-400" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-blue-800">{totals.total_listings || 0}</p>
                            <p className="text-sm text-blue-600">Anunțuri totale</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-emerald-50 border-emerald-200" onClick={() => onNavigate('visitors')}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Globe className="w-8 h-8 text-emerald-500" />
                                <ArrowRight className="w-4 h-4 text-emerald-400" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-emerald-800">{totals.today_unique_visitors || 0}</p>
                            <p className="text-sm text-emerald-600">Vizitatori unici azi</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-red-50 border-red-200" onClick={() => onNavigate('user-reports')}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Flag className="w-8 h-8 text-red-500" />
                                <ArrowRight className="w-4 h-4 text-red-400" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-red-800">{totals.total_reports || 0}</p>
                            <p className="text-sm text-red-600">Număr de raportări</p>
                            <p className="text-xs text-red-500 mt-1">
                                {totals.listing_reports || 0} piese • {totals.user_reports || 0} utilizatori
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-orange-50 border-orange-200" onClick={() => onNavigate('disputes')}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <AlertTriangle className="w-8 h-8 text-orange-500" />
                                <ArrowRight className="w-4 h-4 text-orange-400" />
                            </div>
                            <p className="text-2xl font-bold mt-2 text-orange-800">{totals.pending_disputes || 0}</p>
                            <p className="text-sm text-orange-600">Dispute deschise</p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Counts Overview - Licitații/Vânzări/Comenzi */}
            <div>
                <h3 className="text-lg font-semibold mb-4">Sumar Activitate</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-amber-50 border-amber-200" onClick={() => onNavigate('orders', { status: 'open' })}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Package className="w-5 h-5 text-amber-600" />
                                    <span className="text-sm text-amber-700">Comenzi deschise</span>
                                </div>
                                <ArrowRight className="w-4 h-4 text-amber-400" />
                            </div>
                            <p className="text-2xl font-bold text-amber-800 mt-1">{totals.open_orders || 0}</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-emerald-50 border-emerald-200" onClick={() => onNavigate('listings', { type: 'sale', status: 'sold' })}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                    <span className="text-sm text-emerald-700">Vânzări finalizate</span>
                                </div>
                                <ArrowRight className="w-4 h-4 text-emerald-400" />
                            </div>
                            <p className="text-2xl font-bold text-emerald-800 mt-1">{totals.finished_sales_count || totals.sold_sales || 0}</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-blue-50 border-blue-200" onClick={() => onNavigate('listings', { type: 'auction', status: 'active' })}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Gavel className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm text-blue-700">Licitații deschise</span>
                                </div>
                                <ArrowRight className="w-4 h-4 text-blue-400" />
                            </div>
                            <p className="text-2xl font-bold text-blue-800 mt-1">{totals.open_auctions_count || totals.active_auctions || 0}</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:shadow-md transition-shadow bg-purple-50 border-purple-200" onClick={() => onNavigate('listings', { type: 'auction', status: 'sold' })}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-purple-600" />
                                    <span className="text-sm text-purple-700">Licitații finalizate</span>
                                </div>
                                <ArrowRight className="w-4 h-4 text-purple-400" />
                            </div>
                            <p className="text-2xl font-bold text-purple-800 mt-1">{totals.finished_auctions_count || totals.sold_auctions || 0}</p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Sales & Auctions Value Overview */}
            <div>
                <h3 className="text-lg font-semibold mb-4">Valori Totale</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-amber-50 border-amber-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <Package className="w-5 h-5 text-amber-600" />
                                <span className="text-sm text-amber-700">Valoare vânzări deschise</span>
                            </div>
                            <p className="text-2xl font-bold text-amber-800 mt-1">{formatCurrency(totals.active_sales_value || 0)}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                                <span className="text-sm text-emerald-700">Valoare vânzări finalizate</span>
                            </div>
                            <p className="text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(totals.completed_sales_value || 0)}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <Gavel className="w-5 h-5 text-blue-600" />
                                <span className="text-sm text-blue-700">Valoare licitații deschise</span>
                            </div>
                            <p className="text-2xl font-bold text-blue-800 mt-1">{formatCurrency(totals.active_auctions_value || 0)}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-purple-600" />
                                <span className="text-sm text-purple-700">Valoare licitații finalizate</span>
                            </div>
                            <p className="text-2xl font-bold text-purple-800 mt-1">{formatCurrency(totals.completed_auctions_value || 0)}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-red-50 border-red-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2">
                                <XCircle className="w-5 h-5 text-red-600" />
                                <span className="text-sm text-red-700">Valoare vânzări anulate</span>
                            </div>
                            <p className="text-2xl font-bold text-red-800 mt-1">{formatCurrency(totals.cancelled_orders_value || 0)}</p>
                            <p className="text-xs text-red-500 mt-1">{totals.cancelled_orders_count || 0} comenzi anulate</p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Email Digest Status */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Rezumat Listări Noi (Digest)
                    </h3>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchDigestStatus}
                        disabled={loadingDigest}
                    >
                        {loadingDigest ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4" />
                        )}
                    </Button>
                </div>
                
                {digestStatus && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Subscribers Card */}
                        <Card className="bg-blue-50 border-blue-200">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-blue-700 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Abonați
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-blue-600">Zilnic:</span>
                                        <span className="font-medium text-blue-800">{digestStatus.subscribers?.daily || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-blue-600">Săptămânal:</span>
                                        <span className="font-medium text-blue-800">{digestStatus.subscribers?.weekly || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-blue-600">Instant:</span>
                                        <span className="font-medium text-blue-800">{digestStatus.subscribers?.instant || 0}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Queue Card */}
                        <Card className="bg-amber-50 border-amber-200">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-amber-700 flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    Coadă
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-amber-600">În așteptare (zilnic):</span>
                                        <span className="font-medium text-amber-800">{digestStatus.queue?.daily_pending || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-amber-600">În așteptare (săpt.):</span>
                                        <span className="font-medium text-amber-800">{digestStatus.queue?.weekly_pending || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-amber-600">Trimise (24h):</span>
                                        <span className="font-medium text-amber-800">{digestStatus.queue?.recent_processed_24h || 0}</span>
                                    </div>
                                </div>
                                {(digestStatus.queue?.total_pending || 0) > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full mt-3"
                                        onClick={forceProcessDigest}
                                        disabled={forcingProcess}
                                    >
                                        {forcingProcess ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Procesează acum
                                    </Button>
                                )}
                            </CardContent>
                        </Card>

                        {/* Test & Schedule Card */}
                        <Card className="bg-emerald-50 border-emerald-200">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-emerald-700 flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Program & Test
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="space-y-1 text-xs text-emerald-600 mb-3">
                                    <p>📅 Zilnic: {digestStatus.schedule?.daily_time}</p>
                                    <p>📆 Săptămânal: {digestStatus.schedule?.weekly_day}</p>
                                </div>
                                <div className="space-y-2">
                                    <Input
                                        placeholder="Email pentru test"
                                        value={testEmail}
                                        onChange={(e) => setTestEmail(e.target.value)}
                                        className="h-8 text-sm"
                                    />
                                    <Button
                                        variant="default"
                                        size="sm"
                                        className="w-full"
                                        onClick={sendTestDigest}
                                        disabled={sendingTest || !testEmail}
                                    >
                                        {sendingTest ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Trimite test
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>

            {/* Period Selector for Charts */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Analize Financiare</h3>
                <Select value={chartPeriod} onValueChange={setChartPeriod}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Selectează perioada" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7d">Ultimele 7 zile</SelectItem>
                        <SelectItem value="30d">Ultimele 30 zile</SelectItem>
                        <SelectItem value="90d">Ultimele 90 zile</SelectItem>
                        <SelectItem value="180d">Ultimele 6 luni</SelectItem>
                        <SelectItem value="1y">Ultimul an</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Money Flow Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Flux Financiar ({getPeriodLabel(chartPeriod)})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={dashboard?.money_flow?.money_in || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                                <Area type="monotone" dataKey="amount" name="Bani intrați" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Profit Zilnic ({getPeriodLabel(chartPeriod)})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={dashboard?.money_flow?.profit || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Line type="monotone" dataKey="amount" name="Profit" stroke="#3b82f6" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminDashboardTab;
